import classNames from 'classnames';
import React from 'react';

import { Tooltip } from '@opendoor/bricks';
import { CtaButton } from '@opendoor/bricks';
import { escapeRegExp, formatPhoneNumberInput } from '../../helpers/formatUtils';

import './AccountLoginAndRegistrationFormStyles.less';

export interface IAccountRegistrationFormProps {
  disableEmail?: boolean;
  email?: string;
  emailTooltip?: string;
  headerText?: string;
  subHeaderText?: string;
  loading?: boolean;
  name?: string;
  onSubmit: (
    email: string,
    name: string,
    password: string,
    phone: string,
    rememberMe: boolean,
  ) => void;
  phone?: string;
  registrationError?: boolean | string;
  showInline?: boolean;
  verifyEmail?: boolean;
}

export interface IAccountRegistrationFormState {
  email: string;
  name: string;
  password: string;
  phone: string;
  rememberMe: boolean;
  verificationEmail: string;
}

const NO_OP = () => undefined;

export default class AccountRegistrationForm extends React.Component<
  IAccountRegistrationFormProps,
  IAccountRegistrationFormState
> {
  public static defaultProps: Partial<IAccountRegistrationFormProps> = {
    disableEmail: false,
    email: '',
    headerText: 'Create an Opendoor account',
    name: '',
    phone: '',
    showInline: false,
    verifyEmail: true,
  };

  public state: IAccountRegistrationFormState = {
    email: this.props.email,
    name: this.props.name,
    password: '',
    phone: this.props.phone,
    rememberMe: true,
    verificationEmail: '',
  };

  public verificationEmailField: HTMLInputElement;

  public componentWillReceiveProps(nextProps: IAccountRegistrationFormProps) {
    const { email, name, phone } = this.props;
    if (nextProps.email && email !== nextProps.email) {
      this.setState({ email: nextProps.email });
    }

    if (nextProps.name && name !== nextProps.name) {
      this.setState({ name: nextProps.name });
    }

    if (nextProps.phone && phone !== nextProps.phone) {
      this.setState({ phone: nextProps.phone });
    }
  }

  public handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: e.target.value });
  };

  public handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      phone: e.target.value.replace(/[^\d]+/g, ''),
    });
  };

  public handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity('');
    this.verificationEmailField && this.verificationEmailField.setCustomValidity('');
    this.setState({ email: e.target.value.replace(/\s/g, '') });
  };

  public handleChangeVerificationEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity('');
    this.setState({ verificationEmail: e.target.value.replace(/\s/g, '') });
  };

  public handleInvalidVerificationEmail = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.target.setCustomValidity('The email fields must match.');
  };

  public handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity('');
    this.setState({ password: e.target.value });
  };

  public handleInvalidPassword = (e: React.InvalidEvent<HTMLInputElement>) => {
    // see devise.rb
    e.target.setCustomValidity('Your password must be 5 to 128 characters long.');
  };

  public handleChangeRememberMe = () => {
    const { rememberMe } = this.state;
    this.setState({ rememberMe: !rememberMe });
  };

  public handleSubmit = e => {
    e.preventDefault();
    const { onSubmit } = this.props;
    const { email, name, password, phone, rememberMe } = this.state;
    onSubmit(email, name.trim(), password, phone, rememberMe);
  };

  public render() {
    const {
      disableEmail,
      emailTooltip,
      headerText,
      subHeaderText,
      loading,
      registrationError,
      showInline,
      verifyEmail,
    } = this.props;
    const { email, name, password, phone, rememberMe, verificationEmail } = this.state;

    return (
      <form
        className={classNames('account-registration-form', {
          'account-registration-form--inline': showInline,
        })}
        onSubmit={this.handleSubmit}
      >
        {!showInline && (
          <div>
            <h1 className="login-header text-center">{headerText}</h1>
            {subHeaderText && (
              <div className="small-margin-top large-margin-bottom text-center">
                {subHeaderText}
              </div>
            )}
          </div>
        )}
        <div className="input-section text-left">
          {!showInline && (
            <div className="input-field">
              <b>Your Name</b>
              <br />
              <input
                name="name"
                onChange={this.handleChangeName}
                placeholder="Full Name"
                required={true}
                type="text"
                value={name}
              />
            </div>
          )}
          <div className="input-field">
            <b>Email address</b>
            {emailTooltip && <Tooltip>{emailTooltip}</Tooltip>}
            <br />
            {disableEmail ? (
              <span className="diabled-email-text">{email}</span>
            ) : (
              <input
                name="email"
                onChange={this.handleChangeEmail}
                placeholder="Email"
                required={true}
                type="email"
                value={email}
              />
            )}
          </div>
          {verifyEmail &&
            email && (
              <div className="input-field">
                <b>Verify Email address</b>
                <br />
                <input
                  name="verification-email"
                  onChange={this.handleChangeVerificationEmail}
                  onInvalid={this.handleInvalidVerificationEmail}
                  pattern={escapeRegExp(email)}
                  placeholder="Email"
                  ref={r => (this.verificationEmailField = r)}
                  required={true}
                  type="email"
                  value={verificationEmail}
                />
              </div>
            )}
          {!showInline && (
            <div className="input-field">
              <b>Phone</b>
              <br />
              <input
                name="phone"
                onChange={this.handleChangePhone}
                placeholder="Phone"
                required={true}
                type="tel"
                value={formatPhoneNumberInput(phone)}
              />
            </div>
          )}
          <div className="input-field">
            <b>Password</b>
            <br />
            <input
              name="password"
              onChange={this.handleChangePassword}
              onInvalid={this.handleInvalidPassword}
              pattern=".{5,128}"
              placeholder="Password"
              required={true}
              type="password"
              value={password}
            />
          </div>
          {!showInline && (
            <div className="input-field">
              <input
                checked={rememberMe}
                name="remember-me"
                onChange={this.handleChangeRememberMe}
                type="checkbox"
              />
              <b>Remember Me</b>
            </div>
          )}
          {registrationError && (
            <div>
              <div className="text-red">
                {typeof registrationError === 'string' ? registrationError : 'Invalid inputs.'}
              </div>
            </div>
          )}
          <div className="submit-button">
            <CtaButton actionText="Sign up" loading={loading} onClick={NO_OP} type="submit" />
          </div>
        </div>
      </form>
    );
  }
}
